/**
 * Imperative condition builder — the 3 blocks (Phase 3).
 *
 *   add_condition    — append one leaf to a target entity's buffer
 *   condition_group  — balanced grouping (replaces parentheses)
 *   clear_conditions — empty a target's buffer
 *
 * Generators emit calls to the runtime methods on the page component
 * (this.addCondition / this.beginConditionGroup / this.endConditionGroup /
 * this.clearConditions) — see ui-editor.component.ts + helpers/condition-buffer.ts.
 *
 * TARGET / FIELD are CLICKABLE: their showEditor_ opens the Atlas picker (Step 3.2),
 * mirroring the NonEditableFieldVariable pattern used by the AOB blocks. The visible
 * field text is a human label; the machine values (routing key, relative path,
 * source, dataType, aaobField) live in block.data as JSON — set by onBlockAtlasSelect,
 * read here by the generators.
 *
 * Design: docs/plans/imperative-condition-builder.md.
 */
import * as Blockly from 'blockly-tradais';
import { javascriptGenerator } from 'blockly-tradais/javascript';
import { OPS_FOR_TYPE, AAOB_OPS_FOR_TYPE, AAOB_VALUE_TYPES, VALUE_CHECK_FOR_TYPE, LIST_OUTPUT_TYPES, conditionEffectSentence } from './condition-targets';

/**
 * Fallback operator list — shown only when the field's `dataType` is unknown, which in practice means a
 * Forge-authored condition (the compiler stamps `dataType` for AAOB only). A human who picks a field gets
 * the typed list from OPS_FOR_TYPE instead.
 *
 * NO `in`/`_in` HERE, deliberately. On a REGULAR column `_in` cannot be given a legal value by anything in
 * this platform: no variable type holds a list of scalars (String/Number/Boolean/Entity/EntityList — and an
 * EntityList holds RECORDS), and the compiler has no array-literal case, so a literal list compiles to a
 * comma-joined TEXT block. Every route therefore produces a value Hasura rejects. Offering the operator only
 * invited that — it is how a Forge plan came to filter a uuid column with an array of farmer rows.
 * AAOB is the exception and keeps its own `_in` (AAOB_OPS_FOR_TYPE), which is implemented for real:
 * translateAAOBCache expands it into an `_or` of `_contains`.
 * UPDATE 2026-10-06: a String field now offers `in` / `not in` through OPS_FOR_TYPE — the list is given as
 * comma-separated text and split at runtime (toInList), which is the legal value this note said was missing.
 * This fallback list (a field with no known type, i.e. a Forge-authored condition) still has neither.
 */
const OPERATORS: [string, string][] = [
  ['=', '_eq'], ['≠', '_neq'], ['<', '_lt'], ['≤', '_lte'], ['>', '_gt'], ['≥', '_gte'],
  ['contains', '_ilike'], ['is null', '_isnull_true'], ['is not null', '_isnull_false'],
];

const CONNECTORS: [string, string][] = [['&', 'AND'], ['||', 'OR']];

/** "dynamic" sentinel for the OP / CONNECTOR dropdowns — selecting it reveals a String value socket so the
 *  operator/connector can be typed (= != < …) or driven by a variable. Resolved + validated at runtime
 *  (mapDynamicOp / normalizeConnector in condition-buffer.ts); an unrecognized symbol drops the condition. */
const DYNAMIC_VALUE = '__dynamic__';
const DYNAMIC_OPTION: [string, string] = ['✎ dynamic…', DYNAMIC_VALUE];
const CONNECTORS_DYN: [string, string][] = [...CONNECTORS, DYNAMIC_OPTION];
/** The Missing value choice (zavo_Sep_01). `'legacy'` = a block saved before the choice existed; it is only
 *  offered while a block still holds it, and its generated code is byte-identical to before. */
const MISSING_CHOICES: [string, string][] = [['this condition is false', 'false'], ['skip this condition', 'skip']];
const MISSING_LEGACY: [string, string] = ['as before', 'legacy'];
/** Hover legends (block tooltip) shown while a dropdown = "dynamic". One row per symbol — the quoted
 *  symbol is what to type, the text after it is what it does — so they never run together. */
const DYNOP_LEGEND =
  'Dynamic operator: type one of these, or plug in a variable:\n\n' +
  '  "="         equals\n' +
  '  "!="        not equal\n' +
  '  "<"         less than\n' +
  '  "<="        less than or equal\n' +
  '  ">"         greater than\n' +
  '  ">="        greater than or equal\n' +
  '  "in"        is any value in a list\n' +
  '  "nin"       is none of a list\n' +
  '  "contains"  text contains\n' +
  '  "between"   from the value to the "to" value, both ends included (numbers and dates)\n\n' +
  'Type anything else and this condition is not applied (a console warning is logged).';
// Was (until 2026-10-09): no between row.
//   '  "contains"  text contains\n\n' +
//   'Type anything else and this condition is not applied (a console warning is logged).';
const DYNCONN_LEGEND =
  'Dynamic connector — joins this condition to everything above it. Type one of these, or plug in a variable:\n\n' +
  '  "&&"  AND  (this and everything above must match)\n' +
  '  "||"  OR   (this, or everything above, can match)\n\n' +
  'Type anything else and this condition is not applied (a console warning is logged).';
// Was (until 2026-10-06): the legend described a connector that joined the NEXT condition ("every condition must
// match"); it now joins this one to everything above (ADR 0011), so the wording moved with it.
// const DYNCONN_LEGEND =
//   'Dynamic connector: type one of these, or plug in a variable:\n\n' +
//   '  "&&"  AND  (every condition must match)\n' +
//   '  "||"  OR   (any condition can match)\n\n' +
//   'Type anything else and this condition is not applied (a console warning is logged).';

// REMOVED 2026-07-30 — the PARENT/TRIM intent toggle is gone. The TARGET is the disambiguator now (target the
// entity → filter its rows; target the AOB → trim that list). See docs/plans/condition-target-driven-routing.md.
// const INTENTS: [string, string][] = [['filter the rows', 'PARENT'], ['trim the nested list', 'TRIM']];

/** Sort direction (add_order) — the full Hasura/Postgres order_by enum (asc/desc + null placement). */
const DIRECTIONS: [string, string][] = [
  ['ascending', 'asc'],
  ['descending', 'desc'],
  ['ascending, nulls first', 'asc_nulls_first'],
  ['ascending, nulls last', 'asc_nulls_last'],
  ['descending, nulls first', 'desc_nulls_first'],
  ['descending, nulls last', 'desc_nulls_last'],
];

const COLOUR = '#3a6ea5';

/** Value-socket check before a field is picked — matches nothing, so no value block can connect. */
const NO_FIELD_CHECK = '__NOFIELD__';

/** Machine values for a condition block live in block.data (JSON), set by the Atlas picker / Forge compiler. */
function readData(block: any): any {
  try { return JSON.parse(block.data || '{}'); } catch { return {}; }
}

/**
 * Target expression for codegen. Hand-authored blocks carry a pre-resolved `targetKey` (the Atlas
 * had the live schema). Forge-compiled blocks carry an `onPath` ("Student.subjects") resolved at
 * runtime via this.resolveConditionTarget (the compiler has no field-instance ids). null = unconfigured.
 */
function targetExpr(d: any): string | null {
  if (d.targetKey) return JSON.stringify(d.targetKey);
  if (d.onPath) return `this.resolveConditionTarget(${JSON.stringify(d.onPath)})`;
  return null;
}

// REMOVED 2026-07-30 — applyIntent recomputed routing from the PARENT/TRIM toggle. The toggle is gone; the
// picker writes targetKey+path directly from the chosen TARGET. Kept commented per comment-don't-delete.
// function applyIntent(blk: any, intent: 'PARENT' | 'TRIM'): void {
//   let d: any = {};
//   try { d = JSON.parse(blk.data || '{}'); } catch { return; }
//   if (d.entityKey === undefined) return; // not a configured hand block (Forge blocks route via onPath)
//   const r = resolveIntentRouting(d, intent);
//   d.targetKey = r.targetKey; d.onPath = r.onPath; d.path = r.path;
//   blk.data = JSON.stringify(d);
//   const eff = blk.getField && blk.getField('EFFECT');
//   if (eff) eff.setValue(conditionEffectSentence(d));
// }

/** Apply an AAOB value type: store dataType, reset OP to a valid default, re-type + pop the value socket. */
function applyAaobType(blk: any, type: string): void {
  let d: any = {};
  try { d = JSON.parse(blk.data || '{}'); } catch { return; }
  if (d.source !== 'aaob') return;
  d.dataType = type;
  blk.data = JSON.stringify(d);
  if (blk.getField && blk.getField('OP')) blk.setFieldValue('_eq', 'OP'); // condition blocks only; valid for every type
  const valInput = blk.getInput && blk.getInput('VALUE');
  if (valInput) {
    const check = (VALUE_CHECK_FOR_TYPE as any)[type] || null;
    valInput.setCheck(check);
    const tb = valInput.connection && valInput.connection.targetBlock && valInput.connection.targetBlock();
    if (tb && check) {
      const outCheck = tb.outputConnection && tb.outputConnection.getCheck && tb.outputConnection.getCheck();
      const ok = !outCheck || outCheck.some((t: string) => check.includes(t));
      if (!ok) tb.unplug(true);
    }
  }
}

/**
 * Lay a block out again after a row was shown or hidden: QUEUED, never immediate (zavo_bug_oct_12).
 *
 * BlockSvg.render() does more than draw this block. It flushes Blockly's WHOLE render queue. The rows are toggled
 * from the CONNECTOR / OP field validators, and a validator also runs while a block is being PASTED: Ctrl+V and
 * Duplicate go through serialization.blocks.append, which builds a tree child-first. The `if`'s comparison is
 * built, initialised and queued for drawing before the add_condition under it exists, and the `if` itself is
 * initialised LAST. A flush in the middle reached the uninitialised `if`, skipped it, and dropped it AND everything
 * already queued under it from the queue for good. So the comparison was never drawn, and the `if` laid itself out
 * around an empty socket (the builder reported that moving it fixed the drawing). queueRender() only marks the
 * block: append draws the finished tree once at its end, and anywhere else the queue is drawn on the next frame.
 * (Opening a saved function is an XML load, which queues every block after the whole tree is built. That is why
 * the original drew fine.)
 */
function relayout(blk: any): void {
  if (blk.rendered && blk.queueRender) blk.queueRender();
}

/** Show/hide a set of inputs by name (used for the dynamic op/connector rows). Safe if a name is absent. */
function toggleRows(blk: any, names: string[], visible: boolean): void {
  let changed = false;
  names.forEach((n) => {
    const r = blk.getInput && blk.getInput(n);
    if (r && r.isVisible() !== visible) { r.setVisible(visible); changed = true; }
  });
  if (changed) relayout(blk);
  // Was (until 2026-10-09): if (changed && blk.rendered && blk.render) blk.render();
  // — render() flushes the whole render queue; run from a validator mid-paste it left the `if` above undrawn (relayout).
}

/**
 * Set a DERIVED label — one the block recomputes from its own state and never serializes (the effect line under
 * add_condition, the value socket's "value"/"from"). It fires NO change event.
 *
 * In this Blockly build Field.setValue(v) fires an UNDOABLE BLOCK_CHANGE even for a label field. The block's
 * onchange re-derives the label, so Ctrl+Z reverted it, the onchange put it back as a NEW undoable event (which
 * also clears redo), and undo never got past the label to the builder's real change. The value is still set and
 * the block still re-renders (Field.setValue re-renders a dirty field whatever `fireChangeEvent` says).
 */
function setDerivedLabel(field: any, text: string): void {
  field.setValue(text, false);
}

/** Whole days is offered only where it means something: a Date & time field and an operator that takes a value. */
function syncWholeDays(blk: any, op: string): void {
  const d = readData(blk);
  const show = (d.source || 'field') === 'field' && d.dataType === 'TimeStamp' && op !== '_isnull_true' && op !== '_isnull_false';
  let changed = false;
  for (const name of ['WHOLEDAYSLABEL', 'WHOLEDAYS']) {
    const f = blk.getField && blk.getField(name);
    if (f && f.isVisible() !== show) { f.setVisible(show); changed = true; }
  }
  if (changed) relayout(blk);
}

/** Sync the conditional rows (AAOB key, intent toggle) + the effect line from block.data — on load/create. */
/** Hide the value socket for unary ops — is-null/is-not-null (fields) + has-key/missing-key (AAOB), which
 *  all use the _isnull_* codes. An existence/null check takes no value to compare. */
function syncValueSocketForOp(blk: any, op: string): void {
  const unary = op === '_isnull_true' || op === '_isnull_false';
  const between = op === '_between';
  const v = blk.getInput && blk.getInput('VALUE');
  if (v && v.isVisible() === unary) { v.setVisible(!unary); relayout(blk); }
  // Was (until 2026-10-09): if (v && v.isVisible() === unary) { v.setVisible(!unary); if (blk.rendered && blk.render) blk.render(); }
  // — the OP validator runs this mid-paste, and render() there left the `if` above undrawn (relayout).
  // between (2026-10-06): the first socket reads "from" and a second one, "to", appears.
  const label = blk.getField && blk.getField('VALUELABEL');
  if (label && label.getText() !== (between ? 'from' : 'value')) setDerivedLabel(label, between ? 'from' : 'value');
  // Was (until 2026-10-06): label.setValue(between ? 'from' : 'value') — fired an undoable BLOCK_CHANGE for a derived label.
  // Was (until 2026-10-09): toggleRows(blk, ['VALUETO'], between);  — "to" only for a picked between.
  // typed between (2026-10-09): in typed mode the "to" socket shows too — used only if the typed operator is between.
  const dynamic = op === DYNAMIC_VALUE;
  const toLabel = blk.getField && blk.getField('VALUETOLABEL');
  const toText = dynamic ? 'to (between only)' : 'to';
  if (toLabel && toLabel.getText() !== toText) setDerivedLabel(toLabel, toText);
  toggleRows(blk, ['VALUETO'], between || dynamic);
  // is-null / has-key take no value, so nothing can be missing — hide the choice with the socket.
  toggleRows(blk, ['MISSINGROW'], !unary);
  syncWholeDays(blk, op);
}
// Was (until 2026-10-06): no between row — replaced because between needs a second socket and a "from" label.
// function syncValueSocketForOp(blk: any, op: string): void {
//   const unary = op === '_isnull_true' || op === '_isnull_false';
//   const v = blk.getInput && blk.getInput('VALUE');
//   if (v && v.isVisible() === unary) { v.setVisible(!unary); if (blk.rendered && blk.render) blk.render(); }
//   // is-null / has-key take no value, so nothing can be missing — hide the choice with the socket.
//   toggleRows(blk, ['MISSINGROW'], !unary);
// }

/**
 * A condition's VALUE socket may never hold a LIST. Rejects one that is dragged in, and one that
 * arrives already connected from generated XML.
 *
 * A condition compares ONE row's column against ONE value. Plug a list in and it reaches Hasura
 * verbatim: an entity list against a uuid reference column fails the whole query with "parsing UUID
 * failed, expected String, but encountered Array", taking the page's data with it. The socket's own
 * type-check does not stop this — VALUE_CHECK_FOR_TYPE only applies when `block.data.dataType` is
 * known, and a Forge-authored FIELD condition never carries one (forge-compiler buildAddCondition
 * stamps `dataType` for AAOB only), so its socket is left permissive by syncConditionVisuals.
 *
 * Unplug rather than delete: the block stays on the canvas next to the warning, so the author can see
 * what was rejected and rebuild the intent (filter by the list's ids, not by the list).
 */
function enforceScalarValueSocket(blk: any): void {
  const valIn = blk.getInput && blk.getInput('VALUE');
  const target = valIn && valIn.connection && valIn.connection.targetBlock && valIn.connection.targetBlock();
  if (!target) { if (blk.setWarningText) blk.setWarningText(null); return; }
  const outCheck = target.outputConnection && target.outputConnection.getCheck && target.outputConnection.getCheck();
  const isList = !!outCheck && outCheck.some((t: string) => LIST_OUTPUT_TYPES.includes(t));
  if (!isList) { if (blk.setWarningText) blk.setWarningText(null); return; }
  target.unplug(true);
  if (target.bumpNeighbours) target.bumpNeighbours();
  if (blk.setWarningText) {
    blk.setWarningText(
      'A condition compares one value, so a list cannot be its value.\n' +
      'To filter by a list, compare the column against a single item, or filter on a scalar the list rows share.'
    );
  }
}

const LOOP_TYPES = new Set(['controls_repeat_ext', 'controls_repeat', 'controls_whileUntil', 'controls_for', 'controls_forEach', 'custom_loop']);
const CONDITION_TYPES = new Set(['add_condition', 'condition_group']);
const targetOf = (b: any): string => { const d = readData(b); return d.targetKey || d.onPath || ''; };

/**
 * A block names its target in one of two ways, and they are not interchangeable. A HAND block carries the
 * routing key (`targetKey`, an opaque id) and usually the path too (`onPath`); a FORGE block carries ONLY
 * `onPath`, which the generator resolves at run time (targetExpr). So comparing `targetKey || onPath` as one
 * string calls the same target two different ones whenever a hand block meets a Forge block — which is the
 * ordinary Forge-builds-it, builder-adjusts-it flow.
 *
 *   certainlySameTarget — both name it the same way and the names agree.
 *   mayShareTarget      — that, or they name it in ways that cannot be compared (a key against a bare path).
 *
 * "It may join" is a reason to SHOW the connector; only "it certainly does" is a reason to HIDE it.
 */
const certainlySameTarget = (a: any, b: any): boolean => {
  const da = readData(a), db = readData(b);
  return (!!da.targetKey && !!db.targetKey && da.targetKey === db.targetKey) ||
    (!!da.onPath && !!db.onPath && da.onPath === db.onPath);
};
const mayShareTarget = (a: any, b: any): boolean => {
  if (certainlySameTarget(a, b)) return true;
  const da = readData(a), db = readData(b);
  const keyOnly = (d: any) => !!d.targetKey && !d.onPath;
  const pathOnly = (d: any) => !!d.onPath && !d.targetKey;
  return (keyOnly(da) && pathOnly(db)) || (pathOnly(da) && keyOnly(db));
};

/**
 * Why this block's connector is CERTAIN to do nothing (ADR 0011) — nothing on its target can come before it in this
 * run — or null when it may matter. 'group' = first inside a group on its target; 'first' = first in its stack,
 * or right after a clear. A loop around it, an async group around it (its TRY / CATCH / FINALLY run one after
 * another, TRY in the background), an earlier condition that may be on the target (even inside an if), or a
 * dynamic connector (it can drop the condition outright) might matter — so those leave it visible.
 */
type IgnoredBecause = 'first' | 'group' | null;

// Was (until 2026-10-06): this was `connectorIsIgnored(b): boolean` — its three certain cases `return true`, and the loop
// check was `if (LOOP_TYPES.has(p.type)) return false;`. The reason is now told apart so the line under the block can
// word it (joinPhrase), and an async group counts like a loop. `connectorIsIgnored` below keeps the boolean.
function connectorIgnoredBecause(b: any): IgnoredBecause {
  if (!targetOf(b)) return null;
  // A dynamic connector is resolved at run time and an unrecognised one drops the whole condition, wherever
  // the block sits — hiding it would hide the row (and what is plugged into it) that explains why.
  if (b.getFieldValue && b.getFieldValue('CONNECTOR') === DYNAMIC_VALUE) return null;
  for (let p = b.getSurroundParent && b.getSurroundParent(); p; p = p.getSurroundParent()) {
    // Groups are per target: only a group on THIS target starts a fresh buffer for it (condition-buffer.ts).
    if (p.type === 'condition_group' && certainlySameTarget(p, b)) break;
    // Was (until 2026-10-06): if (p.type === 'condition_group') break;
    // — any group, whatever its target, ended the search, so a loop above a group for ANOTHER target was skipped.
    if (LOOP_TYPES.has(p.type) || p.type === 'async_function_wrapper') return null;
  }
  let cur = b;
  for (;;) {
    const prev = cur.getPreviousBlock && cur.getPreviousBlock();
    if (!prev) return 'first';
    if (prev.getNextBlock() !== cur) {
      // First statement inside `prev`: first in its group only if the group is certainly on this target; a group
      // for another target is stepped out of, and what is above it still counts.
      if (prev.type === 'condition_group' && certainlySameTarget(prev, b)) return 'group';
      // Was (until 2026-10-06): if (prev.type === 'condition_group') return true;  — a group for another target hid it too.
      cur = prev;
      continue;
    }
    if (CONDITION_TYPES.has(prev.type) && mayShareTarget(prev, b)) return null;
    if (prev.type === 'clear_conditions' && certainlySameTarget(prev, b)) return 'first';
    // Was (until 2026-10-06): `targetOf(prev) === tk` for both lines above (and `targetOf(x) === tk` below) — one string
    // for "key or path", so a hand block and a Forge block on the same target looked like different targets.
    // Inside `prev` only: Blockly counts the block AFTER prev (the chain that holds `cur` itself) among prev's
    // descendants, so it is left out — otherwise anything above `cur` would always see `cur`'s own target.
    const after = prev.getNextBlock();
    const inside = prev.getChildren(false).some((c: any) => c !== after &&
      c.getDescendants(false).some((x: any) => CONDITION_TYPES.has(x.type) && mayShareTarget(x, b)));
    if (inside) return null;
    // Was (until 2026-10-06): prev.getDescendants(false).some(x => x !== prev && CONDITION_TYPES.has(x.type) && targetOf(x) === tk)
    // — getDescendants includes prev's NEXT chain, which holds `cur` itself, so a same-target block always saw itself
    // and no connector under a non-condition block could ever be hidden.
    cur = prev;
  }
}

/** True only when this block's connector is CERTAIN to do nothing (ADR 0011). See connectorIgnoredBecause. */
export function connectorIsIgnored(b: any): boolean {
  return connectorIgnoredBecause(b) !== null;
}

/** The join half of the line under the block. */
function joinPhrase(b: any, why: IgnoredBecause): string {
  if (why === 'group') return 'the first condition in this group';
  if (why) {
    // A hand block carries `entityPath`; a Forge block carries ONLY `onPath` ("Student.subjects"). Either way the
    // target's own name is the last segment.
    const d = readData(b);
    const name = String(d.entityPath || d.onPath || 'rows').split('.').pop();
    return `the first condition for ${name}`;
  }
  const c = b.getFieldValue && b.getFieldValue('CONNECTOR');
  if (c === DYNAMIC_VALUE) return 'joined to everything above by the connector you give';
  return `joined with ${c === 'OR' ? 'OR' : 'AND'} to everything above`;
}
// Was (until 2026-10-06): function joinPhrase(b: any, ignored: boolean): string {
//   if (ignored) {
//     const name = String(readData(b).entityPath || 'rows').split('.').pop();
//     return `the first condition for ${name}`;
//   }
//   …
// }
// — a Forge block has no entityPath, so it read "the first condition for rows"; and a block first inside a group
// said "for <target>", which is wrong there.

/** Show the connector only where it can matter, and keep the line under the block in step. */
export function syncConnector(b: any): void {
  const f = b.getField && b.getField('CONNECTOR');
  if (!f) return; // add_order shares syncConditionVisuals but has no connector
  const why = connectorIgnoredBecause(b);
  const ignored = why !== null;
  let changed = false;
  if (f.isVisible() === ignored) { f.setVisible(!ignored); changed = true; }
  if (b.type === 'add_condition') {
    toggleRows(b, ['DYNCONN'], !ignored && b.getFieldValue('CONNECTOR') === DYNAMIC_VALUE);
    const eff = b.getField('EFFECT');
    const opNow = b.getFieldValue('OP') || '_eq';
    const unaryNow = opNow === '_isnull_true' || opNow === '_isnull_false';
    // Was (until 2026-10-09): no whole-days clause.
    // if (eff) setDerivedLabel(eff, conditionEffectSentence(readData(b), unaryNow ? undefined : b.getFieldValue('ONMISSING'), joinPhrase(b, why)));
    const dd = readData(b);
    const wholeDaysNow = (dd.source || 'field') === 'field' && dd.dataType === 'TimeStamp' && !unaryNow && b.getFieldValue('WHOLEDAYS') === 'TRUE';
    if (eff) setDerivedLabel(eff, conditionEffectSentence(dd, unaryNow ? undefined : b.getFieldValue('ONMISSING'), joinPhrase(b, why), wholeDaysNow));
    // Was (until 2026-10-06): eff.setValue(conditionEffectSentence(…, joinPhrase(b, ignored))) — an undoable BLOCK_CHANGE for a
    // derived label, so undo got stuck re-reverting it (see setDerivedLabel).
  }
  if (changed) relayout(b);
  // Was (until 2026-10-09): if (changed && b.rendered && b.render) b.render();
  // — this runs from change events, after a paste is built, so it was not the trigger; queued all the same (relayout).
}

function syncConditionVisuals(blk: any): void {
  let d: any = {};
  try { d = JSON.parse(blk.data || '{}'); } catch { /* unconfigured */ }
  const src = d.source || 'field';
  const keyRow = blk.getInput && blk.getInput('KEY');
  if (keyRow && keyRow.isVisible() !== (src === 'aaob')) keyRow.setVisible(src === 'aaob');
  const typeRow = blk.getInput && blk.getInput('TYPEROW');
  if (typeRow && typeRow.isVisible() !== (src === 'aaob')) typeRow.setVisible(src === 'aaob');
  // (INTENTROW removed 2026-07-30 — the PARENT/TRIM toggle is gone; TARGET disambiguates.)
  syncValueSocketForOp(blk, (blk.getFieldValue && blk.getFieldValue('OP')) || '_eq');
  // Restore the VALUE socket's type-check from block.data on load/change (it's otherwise only set on
  // field-pick, so a reopened condition would keep the permissive default and lose type-safety).
  const valIn = blk.getInput && blk.getInput('VALUE');
  if (valIn) valIn.setCheck(d.dataType ? ((VALUE_CHECK_FOR_TYPE as any)[d.dataType] || null) : null);
  const toIn = blk.getInput && blk.getInput('VALUETO');
  if (toIn) toIn.setCheck(d.dataType ? ((VALUE_CHECK_FOR_TYPE as any)[d.dataType] || null) : null);
  // Runs AFTER the check is restored, so it only has to catch what the check cannot: a list in a
  // socket left permissive because the field's dataType is unknown (every Forge-authored condition).
  enforceScalarValueSocket(blk);
  toggleRows(blk, ['DYNOP'], (blk.getFieldValue && blk.getFieldValue('OP')) === DYNAMIC_VALUE);
  // Was (until 2026-10-06): effect line set here; syncConnector now adds the join (and owns the DYNCONN row, which
  // is shown only where the connector itself is).
  // toggleRows(blk, ['DYNCONN'], (blk.getFieldValue && blk.getFieldValue('CONNECTOR')) === DYNAMIC_VALUE);
  // const eff = blk.getField && blk.getField('EFFECT');
  // const opNow = (blk.getFieldValue && blk.getFieldValue('OP')) || '_eq';
  // const unaryNow = opNow === '_isnull_true' || opNow === '_isnull_false';
  // if (eff) eff.setValue(conditionEffectSentence(d, unaryNow ? undefined : (blk.getFieldValue && blk.getFieldValue('ONMISSING'))));
  syncConnector(blk);
  relayout(blk);
  // Was (until 2026-10-09): if (blk.rendered && blk.render) blk.render();
  // — this runs from change events, after a paste is built, so it was not the trigger; queued all the same (relayout).
}

export function registerConditionBlocks(componentRef: any): void {
  // Clickable label fields: showEditor_ opens Atlas instead of an inline text editor.
  const openStep = (field: any, step: 'VAR' | 'COLUMN') => {
    const blk: any = field.getSourceBlock();
    if (!blk || !componentRef) return;
    componentRef.selectedBlock = blk;
    componentRef.selectedBlockFlag = true;
    componentRef.variableToBeAdded = componentRef.variableToBeAdded || {};
    componentRef.variableToBeAdded.allowedType = 'CONDITION_TARGET';
    componentRef.blockAtlasField = step; // 'VAR' = pick target, 'COLUMN' = pick field
    componentRef.openAtlasForBlock();
  };
  class TargetField extends Blockly.FieldTextInput {
    override showEditor_() { openStep(this, 'VAR'); }
  }
  class FieldPickField extends Blockly.FieldTextInput {
    override showEditor_() { openStep(this, 'COLUMN'); }
  }
  // Clickable "ⓘ" help field: tooltips don't render in this editor, so clicking shows the symbol
  // legend (stored on the instance as `legendText`) in a native dialog. Kept serializable (it stores
  // the static 'ⓘ' glyph) — an editable field with SERIALIZABLE=false logs a console warning and is
  // serialized anyway. The legend itself is re-attached in makeHelp on every load (not serialized).
  class HelpField extends Blockly.FieldTextInput {
    override showEditor_() { try { (window as any).alert((this as any).legendText || ''); } catch { /* no-op */ } }
  }
  const makeHelp = (legend: string): any => {
    const f: any = new HelpField('ⓘ');
    f.legendText = legend;
    return f;
  };

  // ── add_condition ──────────────────────────────────────────────────────────
  Blockly.Blocks['add_condition'] = {
    init: function () {
      this.appendDummyInput('MAIN')
        // Connector in front (ADR 0011, 2026-10-06): it joins this condition to everything above it.
        .appendField(new Blockly.FieldDropdown(CONNECTORS_DYN, function (this: any, val: string) {
          const b: any = this.getSourceBlock && this.getSourceBlock();
          if (b) toggleRows(b, ['DYNCONN'], val === DYNAMIC_VALUE);
          return val;
        }), 'CONNECTOR')
        .appendField('add condition to')
        .appendField(new TargetField('(target)'), 'TARGET')
        .appendField('where')
        .appendField(new FieldPickField('(field)'), 'FIELD');
      // Dynamic-connector row, right under the connector it belongs to. Shown only when the connector =
      // "dynamic". Clickable "ⓘ" opens the legend.
      this.appendValueInput('DYNCONN').setCheck('String').appendField('joined by').appendField(makeHelp(DYNCONN_LEGEND), 'DYNCONNHELP');
      // Was (until 2026-10-06): the connector sat at the bottom as "then ▾" — it read as joining the NEXT condition
      // when it joins everything ABOVE this one (ADR 0011). Same field names, so saved blocks load unchanged.
      // this.appendDummyInput('MAIN')
      //   .appendField('add condition to')
      //   .appendField(new TargetField('(target)'), 'TARGET')
      //   .appendField('where')
      //   .appendField(new FieldPickField('(field)'), 'FIELD');
      // (INTENTROW / PARENT-TRIM toggle REMOVED 2026-07-30 — the TARGET is the disambiguator: target the
      // entity → the condition filters its rows; target the AOB → it trims that list. Old saved blocks may
      // still carry an `INTENT` field value in their XML; Blockly ignores a field the block no longer defines,
      // and codegen is unaffected because targetKey+path were already resolved-and-stored at author time.)
      // AAOB row — only shown for AAOB fields. The systemKey is a String VALUE socket (so it can be a
      // literal text block OR a dynamic variable/expression), plus a value TYPE (AAOB values are typed;
      // the type drives the operators, the value socket, and the cache `_type` assertion).
      this.appendValueInput('KEY')
        .setCheck('String')
        .appendField('AAOB key');
      this.appendDummyInput('TYPEROW')
        .appendField('of type')
        .appendField(new Blockly.FieldDropdown(AAOB_VALUE_TYPES, function (this: any, val: string) {
          const b: any = this.getSourceBlock && this.getSourceBlock();
          if (b) applyAaobType(b, val);
          return val;
        }), 'AAOBTYPE');
      // Operator on its own row, after field/key/type and before the value, so AAOB reads
      // key, type, op, value (instead of the op sitting up on the field row). Type-aware: reads
      // block.data source + dataType -> AAOB_OPS_FOR_TYPE for AAOB, OPS_FOR_TYPE otherwise.
      this.appendDummyInput('OPROW')
        .appendField(new Blockly.FieldDropdown(function (this: any) {
          let d: any = {};
          try { d = JSON.parse(this.getSourceBlock()?.data || '{}'); } catch { /* unconfigured */ }
          // zavo_Sep_22: the synthetic "This record's ID" option supports equality only — no ≠, no null
          // checks, no dynamic operator. Fetching the one record by its id is the entire use case.
          if (d.source === 'self') return [['=', '_eq']];
          const table: any = d.source === 'aaob' ? AAOB_OPS_FOR_TYPE : OPS_FOR_TYPE;
          return [...(table[d.dataType || ''] || OPERATORS), DYNAMIC_OPTION];
        }, function (this: any, val: string) {
          const b: any = this.getSourceBlock && this.getSourceBlock();
          if (b) { syncValueSocketForOp(b, val); toggleRows(b, ['DYNOP'], val === DYNAMIC_VALUE); }
          return val;
        }), 'OP');
      // Whole days (2026-10-09): compare by the value's calendar day in project time. Shown only on a Date & time field.
      this.getInput('OPROW')
        .appendField(new Blockly.FieldLabel('whole days'), 'WHOLEDAYSLABEL')
        .appendField(new Blockly.FieldCheckbox('FALSE'), 'WHOLEDAYS');
      // Dynamic-operator row — shown only when OP = "dynamic": a String socket (literal text or a
      // variable) for the operator symbol. The "ⓘ" is CLICKABLE — it opens the symbol legend dialog.
      this.appendValueInput('DYNOP').setCheck('String').appendField('operator').appendField(makeHelp(DYNOP_LEGEND), 'DYNOPHELP');
      // Value socket: accept anything by default so a SAVED value block RECONNECTS on reload. The
      // type-check is (re)applied from block.data on field-pick (onBlockAtlasSelect) and on load/change
      // (syncConditionVisuals). A reject-all default (NO_FIELD_CHECK) dropped saved values on reopen,
      // because the check is set on pick but not restored before the saved value reconnects on load.
      this.appendValueInput('VALUE').setCheck(null).appendField(new Blockly.FieldLabel('value'), 'VALUELABEL');
      // between (2026-10-06): the upper end. Hidden unless OP = between; VALUE then reads "from".
      // Was (until 2026-10-09): this.appendValueInput('VALUETO').setCheck(null).appendField('to');
      // — plain "to"; a typed between needs "to (between only)", so it is now a named FieldLabel.
      this.appendValueInput('VALUETO').setCheck(null).appendField(new Blockly.FieldLabel('to'), 'VALUETOLABEL');
      // Was (until 2026-10-06): this.appendValueInput('VALUE').setCheck(null).appendField('value');
      // — plain text label; between needs it renamed to "from", so it is now a named FieldLabel.
      // Missing value (zavo_Sep_01). Defaults to 'legacy' so a SAVED block — whose XML has no ONMISSING —
      // keeps its behaviour and its exact generated code. The toolbox and the Forge compilers set 'false'.
      // Once the builder picks a real choice, 'as before' leaves the menu for good.
      this.appendDummyInput('MISSINGROW')
        .appendField('if value is missing')
        .appendField(new Blockly.FieldDropdown(function (this: any) {
          const b: any = this.getSourceBlock && this.getSourceBlock();
          return (!b || b.missingLegacyAllowed !== false) ? [...MISSING_CHOICES, MISSING_LEGACY] : MISSING_CHOICES;
        }, function (this: any, val: string) {
          const b: any = this.getSourceBlock && this.getSourceBlock();
          if (b && val !== 'legacy') b.missingLegacyAllowed = false;
          return val;
        }), 'ONMISSING');
      this.setFieldValue('legacy', 'ONMISSING');
      // Was (until 2026-10-06): the connector sat at the bottom as "then ▾" — it read as joining the NEXT condition
      // (ADR 0011: it joins everything ABOVE). It moved to the front of the MAIN row, with its dynamic row under it.
      // this.appendDummyInput()
      //   .appendField('then')
      //   .appendField(new Blockly.FieldDropdown(CONNECTORS_DYN, function (this: any, val: string) {
      //     const b: any = this.getSourceBlock && this.getSourceBlock();
      //     if (b) toggleRows(b, ['DYNCONN'], val === DYNAMIC_VALUE);
      //     return val;
      //   }), 'CONNECTOR');
      // // Dynamic-connector row — shown only when the connector = "dynamic". Clickable "ⓘ" opens the legend.
      // this.appendValueInput('DYNCONN').setCheck('String').appendField('connector').appendField(makeHelp(DYNCONN_LEGEND), 'DYNCONNHELP');
      // Plain-English effect line (derived, not serialized).
      this.appendDummyInput('EFFECTROW')
        .appendField(new Blockly.FieldLabel(''), 'EFFECT');
      this.setInputsInline(false);
      this.getInput('KEY').setVisible(false);
      this.getInput('TYPEROW').setVisible(false);
      ['DYNOP', 'DYNCONN', 'VALUETO'].forEach((n) => this.getInput(n).setVisible(false));
      ['WHOLEDAYSLABEL', 'WHOLEDAYS'].forEach((n) => this.getField(n).setVisible(false));
      // Keep the conditional rows + effect line in sync with block.data on load / create
      // (interactive picks set them directly in onBlockAtlasSelect).
      const blk: any = this;
      this.setOnChange(function (e: any) {
        if (!blk.workspace || blk.isInFlyout || !e) return;
        const E: any = (Blockly as any).Events;
        // BLOCK_MOVE matters too: plugging a value into VALUE is a MOVE, not a CHANGE, so without it
        // enforceScalarValueSocket would never see an interactively dragged-in list — only one that
        // arrived with the XML. oldParentId covers unplugging, which clears a stale warning.
        const relevant = e.type === E.BLOCK_CREATE || e.type === E.FINISHED_LOADING ||
          (e.type === E.BLOCK_CHANGE && e.blockId === blk.id) ||
          (e.type === E.BLOCK_MOVE && (e.newParentId === blk.id || e.oldParentId === blk.id));
        if (relevant) syncConditionVisuals(blk);
        // Moves, new/deleted blocks and target picks elsewhere can change whether this connector matters.
        // A FIELD pick counts too: on an add_condition it is the step that stamps data.targetKey.
        else if (e.type === E.BLOCK_MOVE || e.type === E.BLOCK_CREATE || e.type === E.BLOCK_DELETE ||
          (e.type === E.BLOCK_CHANGE && (e.name === 'TARGET' || e.name === 'FIELD'))) syncConnector(blk);
      });
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(COLOUR);
      // Tooltips don't render in this editor — the dynamic symbol legend is shown via the clickable
      // "ⓘ" help fields on the DYNOP/DYNCONN rows instead.
      this.setTooltip("Append one condition to a target entity's filter buffer.");
      this.setHelpUrl('');
    },
  };

  (javascriptGenerator as any).forBlock['add_condition'] = function (block: any) {
    const d = readData(block);
    const tExpr = targetExpr(d);
    if (!tExpr) return ''; // unconfigured → no-op
    const source = d.source || 'field';
    const opSel = block.getFieldValue('OP');
    const connSel = block.getFieldValue('CONNECTOR');
    const V = (name: string, dflt: string) =>
      (javascriptGenerator as any).valueToCode(block, name, (javascriptGenerator as any).ORDER_ASSIGNMENT) || dflt;

    // Connector — static AND/OR, or a runtime string when "dynamic" is picked (mapped in this.addCondition).
    const connectorArg = connSel === DYNAMIC_VALUE ? V('DYNCONN', "''") : JSON.stringify(connSel);

    // Missing value (zavo_Sep_01). A 'legacy' block adds NOTHING, so its code is byte-identical to before.
    // is-null ops take no value, so they never carry it. `blankIsValue` marks a text field, where '' is real.
    // Computed BEFORE opPart: the ID branch below needs to know whether a real choice is present.
    const onMissing = block.getFieldValue('ONMISSING');
    const unaryOp = opSel === '_isnull_true' || opSel === '_isnull_false';
    const missingPart = (!unaryOp && (onMissing === 'false' || onMissing === 'skip'))
      ? `, onMissing: '${onMissing}'${d.dataType === 'String' ? ', blankIsValue: true' : ''}`
      : '';
    // Whole days (2026-10-09): only on a Date & time field, only when ticked, never for is-null — so every saved block
    // (unticked, or on a field that is not a date) generates exactly the code it always did.
    const wholeDaysPart = (source === 'field' && d.dataType === 'TimeStamp' && !unaryOp && block.getFieldValue('WHOLEDAYS') === 'TRUE')
      ? ', wholeDays: true' : '';

    // op + value fragment (+ opDynamic flag for the dynamic path).
    let opPart: string;
    if (opSel === DYNAMIC_VALUE) {
      // operator supplied at runtime (typed symbol or variable); mapped + validated in this.addCondition.
      // Was (until 2026-10-09): no "to" in typed mode.
      // opPart = `op: ${V('DYNOP', "''")}, value: ${V('VALUE', 'null')}, opDynamic: true`;
      // typed between (2026-10-09): "to" travels only when something is plugged in, so a saved typed block generates
      // exactly the code it always did.
      const toPart = block.getInputTargetBlock && block.getInputTargetBlock('VALUETO') ? `, valueTo: ${V('VALUETO', 'null')}` : '';
      opPart = `op: ${V('DYNOP', "''")}, value: ${V('VALUE', 'null')}${toPart}, opDynamic: true`;
    } else if (opSel === '_between') {
      // between (2026-10-06): one leaf, both ends — condition-buffer.ts betweenToWhere builds _gte/_lte.
      opPart = `op: "_between", value: ${V('VALUE', 'null')}, valueTo: ${V('VALUETO', 'null')}`;
    } else if (source === 'aaob' && (opSel === '_isnull_true' || opSel === '_isnull_false')) {
      // AAOB has no _is_null — map to _has_key (is-not-null = label exists = _has_key true).
      opPart = `op: '_has_key', value: ${opSel === '_isnull_false' ? 'true' : 'false'}`;
    } else if (opSel === '_isnull_true') {
      opPart = `op: '_is_null', value: true`;
    } else if (opSel === '_isnull_false') {
      opPart = `op: '_is_null', value: false`;
    } else if (source !== 'aaob' && d.dataType === 'ID') {
      // Reference/ID column: the VALUE socket holds an ENTITY (VALUE_CHECK_FOR_TYPE.ID = ['ENTITY']). Reduce it to
      // the entity's id — Hasura's uuid column needs a String, not the whole entity object, else the query fails
      // with "parsing UUID failed, expected String, but encountered Object". Mirrors the typed entity_id_conditions
      // generator (query-helper.ts:1101-1104): `.id` when the value code is a %-path (already `.data`-resolved),
      // else `.data.id` for a top-level entity variable.
      // With a real Missing-value choice (false/skip) and a non-unary op, use optional chaining — a `skip`
      // otherwise throws before addCondition ever runs when `.data` is null (e.g. current user, nobody logged
      // in). A legacy block (no choice) keeps today's non-optional access, byte-identical.
      const raw = V('VALUE', 'null');
      const hasChoice = !unaryOp && (onMissing === 'false' || onMissing === 'skip');
      const idExpr = raw === 'null'
        ? 'null'
        : hasChoice
          ? (raw.includes('%') ? `(${raw})?.id` : `(${raw})?.data?.id`)
          : (raw.includes('%') ? `(${raw}).id` : `(${raw}).data.id`);
      opPart = `op: ${JSON.stringify(opSel)}, value: ${idExpr}`;
    } else {
      opPart = `op: ${JSON.stringify(opSel)}, value: ${V('VALUE', 'null')}`;
    }

    let leaf: string;
    if (source === 'aaob') {
      const vt = d.dataType ? `, valueType: ${JSON.stringify(d.dataType)}` : '';
      // systemKey is a String value socket — a literal text block or a dynamic variable/expression.
      leaf =
        `{ source: 'aaob', aaobField: ${JSON.stringify(d.aaobField || '')}, ` +
        `systemKey: ${V('KEY', "''")}${vt}, ${opPart}${missingPart} }`;
    } else {
      // Was (until 2026-10-09): ..., ${opPart}${missingPart} }`
      leaf = `{ source: 'field', path: ${JSON.stringify(d.path || [])}, ${opPart}${missingPart}${wholeDaysPart} }`;
    }

    return `this.addCondition(${tExpr}, ${leaf}, ${connectorArg});\n`;
  };

  // ── condition_group (balanced container — replaces parens) ───────────────────
  Blockly.Blocks['condition_group'] = {
    init: function () {
      this.appendDummyInput()
        .appendField(new Blockly.FieldDropdown(CONNECTORS), 'CONNECTOR')   // in front: joins the whole group to everything above it
        .appendField('group for')
        .appendField(new TargetField('(target)'), 'TARGET');
      // Was (until 2026-10-06): "group for <target> join <&/||>" — the connector sat after the target and read as
      // joining the group's own conditions; it joins the whole group to everything above it (ADR 0011).
      // this.appendDummyInput()
      //   .appendField('group for')
      //   .appendField(new TargetField('(target)'), 'TARGET')
      //   .appendField('join')
      //   .appendField(new Blockly.FieldDropdown(CONNECTORS), 'CONNECTOR');
      this.appendStatementInput('BODY').setCheck(null);
      // Hide the connector where it is certain to do nothing, same as add_condition. UI-only events (selection,
      // clicks, drags) cannot change that, so only the ones that can are let through.
      const grp: any = this;
      this.setOnChange(function (e: any) {
        if (!grp.workspace || grp.isInFlyout || !e) return;
        const E: any = (Blockly as any).Events;
        const relevant = e.type === E.BLOCK_MOVE || e.type === E.BLOCK_CREATE || e.type === E.BLOCK_DELETE ||
          e.type === E.FINISHED_LOADING || (e.type === E.BLOCK_CHANGE && (e.name === 'TARGET' || e.name === 'FIELD'));
        if (relevant) syncConnector(grp);
      });
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(COLOUR);
      this.setTooltip('Group the conditions inside (balanced — replaces parentheses).');
      this.setHelpUrl('');
    },
  };

  (javascriptGenerator as any).forBlock['condition_group'] = function (block: any) {
    const d = readData(block);
    const tExpr = targetExpr(d);
    const connector = block.getFieldValue('CONNECTOR');
    const body = (javascriptGenerator as any).statementToCode(block, 'BODY');
    if (!tExpr) return body; // unconfigured group → emit body without the (no-op) wrappers
    return (
      `this.beginConditionGroup(${tExpr}, ${JSON.stringify(connector)});\n` +
      body +
      `this.endConditionGroup(${tExpr});\n`
    );
  };

  // ── clear_conditions ─────────────────────────────────────────────────────────
  Blockly.Blocks['clear_conditions'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('clear conditions for')
        .appendField(new TargetField('(target)'), 'TARGET');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(COLOUR);
      this.setTooltip("Empty a target entity's filter buffer.");
      this.setHelpUrl('');
    },
  };

  (javascriptGenerator as any).forBlock['clear_conditions'] = function (block: any) {
    const d = readData(block);
    const tExpr = targetExpr(d);
    if (!tExpr) return ''; // unconfigured → no-op
    return `this.clearConditions(${tExpr});\n`;
  };

  // ── add_order (sort) — twin of add_condition: target -> field -> direction (+ AAOB key/type) ──
  Blockly.Blocks['add_order'] = {
    init: function () {
      this.appendDummyInput('MAIN')
        .appendField('add order to')
        .appendField(new TargetField('(target)'), 'TARGET')
        .appendField('by')
        .appendField(new FieldPickField('(field)'), 'FIELD')
        .appendField(new Blockly.FieldDropdown(DIRECTIONS), 'DIR');
      this.appendValueInput('KEY').setCheck('String').appendField('AAOB key');
      this.appendDummyInput('TYPEROW')
        .appendField('of type')
        .appendField(new Blockly.FieldDropdown(AAOB_VALUE_TYPES, function (this: any, val: string) {
          const b: any = this.getSourceBlock && this.getSourceBlock();
          if (b) applyAaobType(b, val);
          return val;
        }), 'AAOBTYPE');
      this.setInputsInline(false);
      this.getInput('KEY').setVisible(false);
      this.getInput('TYPEROW').setVisible(false);
      const blk: any = this;
      this.setOnChange(function (e: any) {
        if (!blk.workspace || blk.isInFlyout || !e) return;
        const E: any = (Blockly as any).Events;
        const relevant = e.type === E.BLOCK_CREATE || e.type === E.FINISHED_LOADING ||
          (e.type === E.BLOCK_CHANGE && e.blockId === blk.id);
        if (relevant) syncConditionVisuals(blk);
      });
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(COLOUR);
      this.setTooltip('Add a sort key to a target. Multiple add-orders = sort priority by block order.');
      this.setHelpUrl('');
    },
  };

  (javascriptGenerator as any).forBlock['add_order'] = function (block: any) {
    const d = readData(block);
    const tExpr = targetExpr(d);
    if (!tExpr) return ''; // unconfigured → no-op
    const dir = block.getFieldValue('DIR') || 'asc';
    const source = d.source || 'field';
    let leaf: string;
    if (source === 'aaob') {
      const keyExpr =
        (javascriptGenerator as any).valueToCode(block, 'KEY', (javascriptGenerator as any).ORDER_ASSIGNMENT) || "''";
      const vt = d.dataType ? `, valueType: ${JSON.stringify(d.dataType)}` : '';
      leaf =
        `{ source: 'aaob', aaobField: ${JSON.stringify(d.aaobField || '')}, ` +
        `systemKey: ${keyExpr}${vt}, direction: ${JSON.stringify(dir)} }`;
    } else {
      leaf = `{ source: 'field', path: ${JSON.stringify(d.path || [])}, direction: ${JSON.stringify(dir)} }`;
    }
    return `this.addOrder(${tExpr}, ${leaf});\n`;
  };

  // ── clear_orders ─────────────────────────────────────────────────────────────
  Blockly.Blocks['clear_orders'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('clear sort for')
        .appendField(new TargetField('(target)'), 'TARGET');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(COLOUR);
      this.setTooltip("Empty a target's sort buffer.");
      this.setHelpUrl('');
    },
  };

  (javascriptGenerator as any).forBlock['clear_orders'] = function (block: any) {
    const d = readData(block);
    const tExpr = targetExpr(d);
    if (!tExpr) return '';
    return `this.clearOrders(${tExpr});\n`;
  };
}
